import { ref, watch, type Ref } from 'vue'
import { PhoneCodes, type PhoneCode } from '@/constants/phone-codes'
import { findPhoneCodeByIsoCode, findPhoneCodeByIsoCodeWithDefault } from '../detect/usePhoneCodeFromCountry'

/**
 * Composable для управління вибором країни та кодом телефону
 * Відповідає за: Single Responsibility - тільки вибір країни та код
 */
export function usePhoneCountrySelection(
    initialCode: string,
    onCodeUpdate?: (code: string) => void
) {
    const phoneCode = ref(initialCode)
    const currentCountryIso = ref<string>('')

    /**
     * Знаходить країну за кодом телефону
     */
    const getCountryByCode = (code: string): PhoneCode => {
        return PhoneCodes.find((p) => p.phoneCode === code) 
            ?? PhoneCodes.find((p) => p.phoneCode === '380')!
    }

    /**
     * Знаходить країну за ISO кодом країни
     * @param isoCode - ISO 3166-1 alpha-2 код країни (наприклад, 'UA', 'US')
     * @returns PhoneCode або null якщо не знайдено
     */
    const getCountryByIsoCode = (isoCode: string | null): PhoneCode | null => {
        return findPhoneCodeByIsoCode(isoCode)
    }

    /**
     * Встановлює код країни за ISO кодом
     * @param isoCode - ISO 3166-1 alpha-2 код країни
     * @param defaultCode - Код телефону за замовчуванням якщо ISO код не знайдено
     */
    const setCodeByIsoCode = (isoCode: string | null, defaultCode: string = '380') => {
        const country = findPhoneCodeByIsoCodeWithDefault(isoCode, defaultCode)
        setCode(country)
    }

    /**
     * Встановлює код країни та ISO код
     */
    const setCode = (countryCode: PhoneCode) => {
        phoneCode.value = countryCode.phoneCode
        currentCountryIso.value = countryCode.isoCode
        onCodeUpdate?.(countryCode.phoneCode)
    }

    /**
     * Ініціалізує код за замовчуванням
     */
    const initializeCode = (code: string) => {
        const country = getCountryByCode(code)
        setCode(country)
    }

    /**
     * Синхронізує код з зовнішнім prop
     */
    const syncCode = (newCode: string) => {
        if (newCode && newCode !== phoneCode.value) {
            const country = getCountryByCode(newCode)
            setCode(country)
        }
    }

    return {
        phoneCode,
        currentCountryIso,
        getCountryByCode,
        getCountryByIsoCode,
        setCode,
        setCodeByIsoCode,
        initializeCode,
        syncCode,
    }
}

